// Kontur Asia - Main entrypoint (styles load via vite_stylesheet_tag in layouts)

// Turbo + Stimulus. goals.js was renamed from analytics.js because ad/tracker
// blockers matched the "analytics.js" request path (in dev Vite serves each
// module at its own URL) and refused it, which aborted this whole module and
// took Turbo + Stimulus down with it. The neutral name is the actual fix.
import * as Turbo from '@hotwired/turbo'
Turbo.start()
import '@/controllers'
import { trackPhoneClick, trackWhatsappClick } from '../lib/goals.js'

// Vue
import { createApp } from 'vue'
import CalculatorWithRequest from '@/components/CalculatorWithRequest.vue'

// Mount Vue components
const mountVueComponents = () => {
  const el = document.getElementById('vue-calculator-request')
  if (el) {
    createApp(CalculatorWithRequest).mount(el)
  }
}

document.addEventListener('DOMContentLoaded', mountVueComponents)
document.addEventListener('turbo:load', mountVueComponents)

// Disable browser's automatic scroll restoration
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual'
}

// Mobile menu toggle function
const toggleMobileMenu = () => {
  const mobileMenuBtn = document.getElementById('mobileMenuBtn')
  const mobileMenu = document.getElementById('mobileMenu')

  if (!mobileMenuBtn || !mobileMenu) return

  const isOpen = !mobileMenu.classList.contains('hidden')
  const spans = mobileMenuBtn.querySelectorAll('span')

  if (isOpen) {
    mobileMenu.classList.add('hidden')
    spans.forEach((span) => {
      span.style.transform = ''
      span.style.opacity = ''
    })
  } else {
    mobileMenu.classList.remove('hidden')
    spans[0].style.transform = 'rotate(45deg) translate(5px, 5px)'
    spans[1].style.opacity = '0'
    spans[2].style.transform = 'rotate(-45deg) translate(5px, -5px)'
  }
}

const closeMobileMenu = () => {
  const mobileMenuBtn = document.getElementById('mobileMenuBtn')
  const mobileMenu = document.getElementById('mobileMenu')

  if (!mobileMenuBtn || !mobileMenu) return

  mobileMenu.classList.add('hidden')
  mobileMenuBtn.querySelectorAll('span').forEach((span) => {
    span.style.transform = ''
    span.style.opacity = ''
  })
}

// Event delegation - attach once to document
document.addEventListener('click', (e) => {
  // Mobile menu button
  if (e.target.closest('#mobileMenuBtn')) {
    e.preventDefault()
    toggleMobileMenu()
    return
  }

  // Mobile menu links
  if (e.target.closest('.mobile-nav-link, .mobile-cta-link')) {
    closeMobileMenu()
  }

  // YM: track phone and WhatsApp clicks
  const telLink = e.target.closest('a[href^="tel:"]')
  if (telLink) {
    trackPhoneClick({ phone: telLink.getAttribute('href').replace('tel:', '') })
  }

  const waLink = e.target.closest('a[href*="wa.me"], a[href*="api.whatsapp.com"]')
  if (waLink) {
    trackWhatsappClick({ href: waLink.getAttribute('href') })
  }

  // Smooth scroll for anchor links
  const anchor = e.target.closest('a[href^="#"]')
  if (anchor) {
    const targetId = anchor.getAttribute('href')
    if (targetId === '#') return

    const target = document.querySelector(targetId)
    if (target) {
      e.preventDefault()
      closeMobileMenu()
      const headerOffset = 70
      const elementPosition = target.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })

      // Update URL with anchor, pause scroll spy during animated scroll
      scrollSpyPaused = true
      setTimeout(() => {
        scrollSpyPaused = false
      }, 1000)
      history.pushState(null, '', targetId)
    }
  }
})

// Header scroll effect
const handleScroll = () => {
  const header = document.getElementById('header')
  if (header) {
    if (window.scrollY > 50) {
      header.classList.add('shadow-lg')
    } else {
      header.classList.remove('shadow-lg')
    }
  }
}

window.addEventListener('scroll', handleScroll)
document.addEventListener('DOMContentLoaded', handleScroll)
document.addEventListener('turbo:load', handleScroll)

// Scroll to anchor from URL on page load
const scrollToHash = (hash) => {
  const target = document.querySelector(hash)
  if (!target) return

  const headerOffset = 70
  const top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset
  window.scrollTo({ top, behavior: 'smooth' })
}

const scrollToHashOnLoad = () => {
  const hash = window.location.hash
  if (!hash || hash === '#') return

  // First attempt — DOM ready
  setTimeout(() => scrollToHash(hash), 100)
  // Second attempt — after Vue mounts (calculator/request sections)
  setTimeout(() => scrollToHash(hash), 800)
}

document.addEventListener('DOMContentLoaded', scrollToHashOnLoad)
document.addEventListener('turbo:load', scrollToHashOnLoad)

// Scroll spy — update URL hash on scroll
let scrollSpyPaused = true

const initScrollSpy = () => {
  const sections = document.querySelectorAll('section[id]')
  if (!sections.length) return

  scrollSpyPaused = true
  setTimeout(() => {
    scrollSpyPaused = false
  }, 1200)

  const observer = new IntersectionObserver(
    (entries) => {
      if (scrollSpyPaused) return

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const newHash = `#${entry.target.id}`
          if (window.location.hash !== newHash) {
            history.replaceState(null, '', newHash)
          }
        }
      })
    },
    { rootMargin: '-50% 0px -50% 0px' }
  )

  sections.forEach((section) => observer.observe(section))
}

document.addEventListener('DOMContentLoaded', initScrollSpy)
document.addEventListener('turbo:load', initScrollSpy)

// Portfolio slider
const initPortfolioSlider = () => {
  const slider = document.getElementById('portfolioSlider')
  const track = document.getElementById('portfolioTrack')
  const prevBtn = document.getElementById('portfolioPrev')
  const nextBtn = document.getElementById('portfolioNext')
  const dotsContainer = document.getElementById('portfolioDots')

  if (!slider || !track || !prevBtn || !nextBtn || !dotsContainer) return

  const slides = track.querySelectorAll('.portfolio-slide')
  let currentIndex = 0
  let slidesPerView = 3

  // Определяем количество слайдов в зависимости от ширины экрана
  const updateSlidesPerView = () => {
    if (window.innerWidth < 640) {
      slidesPerView = 1
    } else if (window.innerWidth < 1024) {
      slidesPerView = 2
    } else {
      slidesPerView = 3
    }
  }

  // Создаём точки навигации
  const createDots = () => {
    dotsContainer.innerHTML = ''
    const totalDots = Math.ceil(slides.length / slidesPerView)
    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('button')
      dot.className =
        'w-3 h-3 rounded-full transition-colors ' +
        (i === 0 ? 'bg-primary' : 'bg-gray-300 hover:bg-gray-400')
      dot.addEventListener('click', () => goToSlide(i))
      dotsContainer.appendChild(dot)
    }
  }

  // Обновляем активную точку
  const updateDots = () => {
    const dots = dotsContainer.querySelectorAll('button')
    const activeIndex = Math.floor(currentIndex / slidesPerView)
    dots.forEach((dot, i) => {
      if (i === activeIndex) {
        dot.className = 'w-3 h-3 rounded-full bg-primary transition-colors'
      } else {
        dot.className = 'w-3 h-3 rounded-full bg-gray-300 hover:bg-gray-400 transition-colors'
      }
    })
  }

  // Переход к слайду
  const goToSlide = (dotIndex) => {
    currentIndex = dotIndex * slidesPerView
    if (currentIndex > slides.length - slidesPerView) {
      currentIndex = slides.length - slidesPerView
    }
    updateSlider()
  }

  // Обновляем позицию слайдера
  const updateSlider = () => {
    const slideWidth = slides[0].offsetWidth + 24 // ширина + gap
    track.style.transform = `translateX(-${currentIndex * slideWidth}px)`
    updateDots()
  }

  // Навигация
  const goNext = () => {
    if (currentIndex < slides.length - slidesPerView) {
      currentIndex++
      updateSlider()
    }
  }

  const goPrev = () => {
    if (currentIndex > 0) {
      currentIndex--
      updateSlider()
    }
  }

  // События
  prevBtn.addEventListener('click', goPrev)
  nextBtn.addEventListener('click', goNext)

  // Инициализация
  updateSlidesPerView()
  createDots()
  updateSlider()

  // Обновление при изменении размера окна
  let resizeTimeout
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout)
    resizeTimeout = setTimeout(() => {
      const prevSlidesPerView = slidesPerView
      updateSlidesPerView()
      if (prevSlidesPerView !== slidesPerView) {
        currentIndex = 0
        createDots()
      }
      updateSlider()
    }, 100)
  })
}

document.addEventListener('DOMContentLoaded', initPortfolioSlider)
document.addEventListener('turbo:load', initPortfolioSlider)
